import { useCallback, useState } from 'react';
import { queryParamToString } from '@moda/portal-stanchions';
import { logger } from '../../lib/logger';
import {
  AuthPayloadSuccess,
  AuthAction,
  AuthError,
  AuthPayload,
  isAuthPayloadEmailVerificationRequired,
  authenticate
} from '../../lib/authenticate';
import { useCookies } from '../useCookies';
import { RECAPTCHA_ACTIONS, useRecaptcha } from '../useRecaptcha';
import { useSessionQueryParams } from '../useSessionQueryParams';
import { useAuthModal } from '../useAuthModal';
import { useEmailVerificationStatusModal } from '../../components/EmailVerificationStatus';
import { CONFIG } from '../../config';
import { useCompleteAuth } from './useCompleteAuth';

export type HandleAuthBaseProps = {
  guest?: false;
  email: string;
  first_name?: string;
  last_name?: string;
  password: string;
  remember?: boolean;
  opted_in?: boolean;
};

type HandleAuthGuestProps = { guest: true; email: string; remember: boolean; opted_in?: boolean };

export type HandleAuthProps = HandleAuthBaseProps | HandleAuthGuestProps;

export const useAuth = (action: AuthAction) => {
  const [error, setError] = useState<AuthError>();
  const [loading, setLoading] = useState(false);

  const { closeAuthModal } = useAuthModal();
  const { handleRecaptchaV3 } = useRecaptcha();
  const {
    cookies: { cartId }
  } = useCookies();
  const {
    params: { bypass_rack_attack: bypassRackAttackQueryParameter }
  } = useSessionQueryParams();
  const { showEmailVerificationStatusModal } = useEmailVerificationStatusModal();
  const completeAuth = useCompleteAuth();

  const bypassRackAttack =
    queryParamToString(bypassRackAttackQueryParameter) || CONFIG.BYPASS_RACK_ATTACK;

  const handleAuth = useCallback(
    ({ remember, ...values }: HandleAuthProps) => {
      const handleError = (error: AuthError) => {
        setError(error);
        setLoading(false);
        logger.info('AUTH_FAILED', error.message || '', {
          code: error.code,
          message: error.message
        });
        throw error;
      };

      const handleSuccess = async (user: AuthPayloadSuccess) => {
        await completeAuth({ user, remember, guest: values.guest, opted_in: values.opted_in });
        setLoading(false);

        return user;
      };

      const handleEmailVerificationRequired = () => {
        if (values.guest)
          throw new Error(
            'Unexpected email verification required response received for guest user.'
          );

        setLoading(false);
        closeAuthModal();
        showEmailVerificationStatusModal({ status: 'pending', user: { ...values, remember } });

        return new Promise<AuthPayloadSuccess>(() => null);
      };

      const handleResponse = (response: AuthPayload) => {
        if (isAuthPayloadEmailVerificationRequired(response))
          return handleEmailVerificationRequired();

        return handleSuccess(response);
      };

      setLoading(true);

      const attributes = { ...values, cart_id: cartId };

      if (bypassRackAttack) {
        return authenticate({
          action,
          attributes,
          bypassRackAttack
        })
          .then(handleResponse)
          .catch(handleError);
      }

      return handleRecaptchaV3(RECAPTCHA_ACTIONS[action]).then((tokenV3: string) =>
        authenticate({
          action,
          attributes: {
            recaptcha: {
              token: tokenV3,
              type: 'v3'
            },
            ...attributes
          }
        })
          .then(handleResponse)
          .catch(error => {
            handleError(error);
            return undefined;
          })
      );
    },
    [
      action,
      bypassRackAttack,
      cartId,
      closeAuthModal,
      completeAuth,
      handleRecaptchaV3,
      showEmailVerificationStatusModal
    ]
  );

  return { error, loading, handleAuth };
};
